(() => {
  if (customElements.get('sg-delivery-information')) return;

  class DeliveryInformation extends HTMLElement {
    connectedCallback() {
      if (this.closest('quick-add-modal')) {
        this.remove();
        return;
      }
      this.form = this.querySelector('[data-delivery-form]');
      this.pin = this.form.querySelector('input[name="delivery_pin"]');
      this.button = this.form.querySelector('button');
      this.result = this.querySelector('[data-delivery-result]');
      this.productInfo = this.closest('product-info');
      this.requestId = (this.requestId || 0) + 1;
      this.onSubmit = this.checkCoverage.bind(this);
      this.onInput = this.resetCheck.bind(this);
      this.onVariantChange = this.handleVariantChange.bind(this);
      this.form.addEventListener('submit', this.onSubmit);
      this.pin.addEventListener('input', this.onInput);
      this.productInfo?.addEventListener('change', this.onVariantChange);
      this.form.hidden = false;
      this.querySelector('[data-delivery-fallback]').hidden = true;
    }

    disconnectedCallback() {
      this.resetCheck();
      this.form?.removeEventListener('submit', this.onSubmit);
      this.pin?.removeEventListener('input', this.onInput);
      this.productInfo?.removeEventListener('change', this.onVariantChange);
    }

    selectedPhysicalVariant() {
      const productForm = this.productInfo?.querySelector('form[id="' + CSS.escape(this.dataset.productFormId) + '"]');
      const variantId = productForm?.elements.namedItem('id')?.value;
      if (this.variantUpdating || !/^\d+$/.test(variantId || '')) return false;
      const selectedVariant = this.productInfo.querySelector('variant-selects [data-selected-variant]');
      if (!selectedVariant) {
        return variantId === this.dataset.initialVariantId && this.dataset.initialPhysical === 'true';
      }
      try {
        const variant = JSON.parse(selectedVariant.textContent);
        return String(variant?.id) === variantId && variant.requires_shipping === true;
      } catch (_) {
        return false;
      }
    }

    handleVariantChange(event) {
      if (event.target.closest('variant-selects')) {
        this.variantUpdating = true;
        this.resetCheck();
        this.hidden = true;
      }
      if (event.target.name === 'id' && event.target.form?.getAttribute('id') === this.dataset.productFormId) {
        this.variantUpdating = false;
        this.resetCheck();
        this.hidden = !this.selectedPhysicalVariant();
      }
    }

    resetCheck() {
      this.requestId = (this.requestId || 0) + 1;
      this.controller?.abort();
      clearTimeout(this.timeout);
      if (this.button) this.button.disabled = false;
      this.form?.removeAttribute('aria-busy');
      this.pin?.removeAttribute('aria-invalid');
      if (this.result) {
        this.result.textContent = '';
        delete this.result.dataset.state;
      }
    }

    showResult(message, state) {
      this.result.textContent = message;
      this.result.dataset.state = state;
    }

    async checkCoverage(event) {
      event.preventDefault();
      if (this.button.disabled) return;
      this.resetCheck();
      const pincode = this.pin.value.trim();
      if (!/^[1-9][0-9]{5}$/.test(pincode)) {
        this.showResult(this.dataset.pinError, 'error');
        this.pin.setAttribute('aria-invalid', 'true');
        this.pin.focus();
        return;
      }
      if (!this.selectedPhysicalVariant()) {
        this.hidden = true;
        return;
      }
      const requestId = this.requestId;
      this.controller = new AbortController();
      this.button.disabled = true;
      this.form.setAttribute('aria-busy', 'true');
      this.showResult(this.dataset.checking, 'checking');
      this.timeout = setTimeout(() => this.controller.abort(), 10000);
      try {
        const response = await fetch('https://bot.satgurucreations.shop/storefront/serviceability', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pincode }),
          credentials: 'omit',
          cache: 'no-store',
          referrerPolicy: 'no-referrer',
          redirect: 'error',
          signal: this.controller.signal,
        });
        if (response.status !== 200) throw new Error('Unavailable');
        const data = await response.json();
        const checkedAt = typeof data?.checked_at === 'string' ? data.checked_at : '';
        const validTimestamp = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])T([01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d{1,6})?(?:Z|\+00:00)$/.test(checkedAt)
          && Number.isFinite(Date.parse(checkedAt))
          && new Date(checkedAt).toISOString().slice(0, 19) === checkedAt.slice(0, 19)
          && Date.now() - Date.parse(checkedAt) <= 16 * 60 * 1000
          && Date.parse(checkedAt) - Date.now() <= 60 * 1000;
        if (!data || data.pincode !== pincode || data.origin !== '400054' || !validTimestamp
          || !['covered', 'not_covered', 'unavailable'].includes(data.status)) throw new Error('Unavailable');
        if (requestId !== this.requestId || this.pin.value.trim() !== pincode) return;
        const messages = { covered: this.dataset.covered, not_covered: this.dataset.notCovered, unavailable: this.dataset.unavailable };
        this.showResult(messages[data.status], data.status);
      } catch (_) {
        if (requestId === this.requestId && this.pin.value.trim() === pincode) {
          this.showResult(this.dataset.unavailable, 'unavailable');
        }
      } finally {
        if (requestId === this.requestId) {
          clearTimeout(this.timeout);
          this.button.disabled = false;
          this.form.removeAttribute('aria-busy');
        }
      }
    }
  }

  customElements.define('sg-delivery-information', DeliveryInformation);
})();
